import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LinearProgress, makeStyles, Typography } from '@material-ui/core'

export interface ProgressBarProps {
  value: number
  color?: string
  onChange?: (value: number) => void
  /**
   * When true, drop the outer row padding + top divider. Use when embedding
   * the bar inside a container that provides its own spacing.
   */
  disablePadding?: boolean
}

const useStyles = makeStyles((theme) => ({
  progressRow: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  progressRowFlush: {
    padding: 0,
    borderTop: 'none'
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  rowLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500
  },
  progressBarContainer: {
    position: 'relative',
    width: '100%',
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75)
  },
  progressBar: {
    height: 14,
    borderRadius: 999,
    backgroundColor: theme.paintscout?.palette?.brand?.Neutral?.P96 ?? '#ece9fb',
    '@supports (background-color: color-mix(in srgb, red, white))': {
      backgroundColor: 'color-mix(in srgb, var(--bar-color, #5b4ee6) 18%, white)'
    }
  },
  progressBarPrimary: {
    backgroundColor: 'var(--bar-color, #5b4ee6)',
    borderRadius: 999,
    transition: 'none'
  },
  progressKnob: {
    position: 'absolute',
    top: '50%',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: 'var(--bar-color, #5b4ee6)',
    border: `3px solid ${theme.palette.background.paper}`,
    transform: 'translate(-50%, -50%)',
    boxShadow: theme.shadows[1],
    pointerEvents: 'none'
  },
  progressPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    backgroundColor: theme.paintscout?.palette?.brand?.Neutral?.P96 ?? '#eef0f4'
  }
}))

export default function ProgressBar({ value, color, onChange, disablePadding }: ProgressBarProps) {
  const classes = useStyles()
  const [localValue, setLocalValue] = useState(value)
  const [dragging, setDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const interactive = !!onChange
  // Stash latest onChange in a ref so the drag closure always commits to the
  // current handler, even if the parent re-renders mid-drag.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    if (!dragging) setLocalValue(value)
  }, [value, dragging])

  const valueFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    if (rect.width <= 0) return 0
    const ratio = (clientX - rect.left) / rect.width
    return Math.max(0, Math.min(100, Math.round(ratio * 100)))
  }, [])

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!interactive) return
    e.preventDefault()
    e.stopPropagation()
    setDragging(true)
    const initial = valueFromClientX(e.clientX)
    setLocalValue(initial)

    const handleMove = (ev: PointerEvent) => {
      setLocalValue(valueFromClientX(ev.clientX))
    }
    const handleUp = (ev: PointerEvent) => {
      const finalValue = valueFromClientX(ev.clientX)
      setLocalValue(finalValue)
      setDragging(false)
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
      onChangeRef.current?.(finalValue)
    }
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
  }

  const colorVarStyle = color ? ({ '--bar-color': color } as React.CSSProperties) : undefined

  return (
    <div
      className={disablePadding ? `${classes.progressRow} ${classes.progressRowFlush}` : classes.progressRow}
      style={colorVarStyle}
    >
      <div className={classes.progressHeader}>
        <Typography className={classes.rowLabel}>Progress</Typography>
        <span className={classes.progressPill}>{Math.round(localValue)}%</span>
      </div>
      <div
        ref={containerRef}
        className={classes.progressBarContainer}
        style={{ cursor: interactive ? 'pointer' : 'default', touchAction: 'none' }}
        onPointerDown={handlePointerDown}
      >
        <LinearProgress
          variant="determinate"
          value={localValue}
          classes={{ root: classes.progressBar, bar: classes.progressBarPrimary }}
        />
        <span
          className={classes.progressKnob}
          style={{ left: `${localValue}%`, cursor: interactive ? (dragging ? 'grabbing' : 'grab') : 'default' }}
        />
      </div>
    </div>
  )
}
